import { useState } from "react";
import { useTranslation } from "react-i18next";
import { updateBreeding } from "../../../../api/breeding";
import { Breeding } from "../../../../generated/types";
import { Button } from "../../../common/Button";
import { sectionToIcon } from "../../../type/default";
import { SetupError } from "../../setup/SetupLayout";
import { BreedingFields, useBreedingForm } from "../BreedingFields";
import { DeleteButton, DetailCard, EditButton, InfoRow } from "./DetailCard";

const breedingToForm = (breeding: Breeding) => ({
  name: breeding.name,
  address: breeding.address,
  address_complementary: breeding.address_complementary ?? "",
  zip_code: breeding.zip_code,
  city: breeding.city,
});

export const BreedingInfoCard = ({
  breeding,
  isPartial,
  canEdit,
  canDelete,
  onSaved,
  onDelete,
}: {
  breeding: Breeding;
  isPartial: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onSaved: () => Promise<void>;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();
  const breedingForm = useBreedingForm(breedingToForm(breeding));
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const startEditing = () => {
    breedingForm.reset(breedingToForm(breeding));
    setError("");
    setIsEditing(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!breedingForm.validate()) return;

    setSubmitting(true);
    const result = await updateBreeding({ ...breeding, ...breedingForm.form });
    if (!result.success) {
      setSubmitting(false);
      setError(t("error-breeding-update-failed"));
      return;
    }
    await onSaved();
    setSubmitting(false);
    setIsEditing(false);
  };

  return (
    <DetailCard
      title={t("breeding-detail-info-title")}
      icon={sectionToIcon.section_breeding}
      action={
        isEditing ? null : (
          <div className="flex">
            {canEdit && <EditButton onClick={startEditing} />}
            {canDelete && (
              <DeleteButton label={t("breeding-delete")} onClick={onDelete} />
            )}
          </div>
        )
      }
    >
      {isEditing ? (
        <form onSubmit={submit}>
          {error && (
            <div className="mb-3">
              <SetupError message={error} />
            </div>
          )}
          <BreedingFields breedingForm={breedingForm} layout="row" />
          <div className="flex justify-end gap-2 pt-3">
            <Button
              size="small"
              type="button"
              variant="secondary"
              onClick={() => setIsEditing(false)}
            >
              {t("button-cancel")}
            </Button>
            <Button type="submit" size="small" disabled={submitting}>
              {submitting ? t("setup-saving") : t("button-save")}
            </Button>
          </div>
        </form>
      ) : (
        <dl>
          {isPartial && (
            <p className="mb-3 text-sm text-grey-medium">
              {t("breeding-detail-partial-share")}
            </p>
          )}
          <InfoRow label={t("breeding-name")} value={breeding.name} />
          {!isPartial && (
            <InfoRow label={t("company-address")} value={breeding.address} />
          )}
          {breeding.address_complementary && (
            <InfoRow
              label={t("company-address-complementary")}
              value={breeding.address_complementary}
            />
          )}
          <InfoRow label={t("company-zip-code")} value={breeding.zip_code} />
          <InfoRow label={t("company-city")} value={breeding.city} />
        </dl>
      )}
    </DetailCard>
  );
};
